import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Close } from '@mui/icons-material';
import { useEmailTemplate } from 'app/context/EmailTemplateContext';

const PreviewDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(2),
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  '& iframe': {
    width: '100%',
    height: '100%',
    border: 'none',
  },
}));

const PreviewDialogTitle = styled(DialogTitle)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  flexShrink: 0,
}));

interface EmailTemplatePreviewDialogProps {
  open: boolean;
  onClose: () => void;
  templateId?: string;
  dealerId?: string;
}

export const EmailTemplatePreviewDialog: React.FC<EmailTemplatePreviewDialogProps> = ({
  open,
  onClose,
  templateId,
  dealerId,
}) => {
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<any>(null);

  const { getEmailTemplateById, selectedTemplate, loading: templateLoading } = useEmailTemplate();

  // Update preview template when selectedTemplate changes
  useEffect(() => {
    if (selectedTemplate && open) {
      setPreviewTemplate(selectedTemplate);
    }
  }, [selectedTemplate, open]);

  // Fetch template when dialog opens and templateId is provided
  useEffect(() => {
    if (open && templateId && dealerId) {
      handleFetchTemplate();
    } else if (!open) {
      // Reset state when dialog closes
      setPreviewTemplate(null);
      setPreviewError(null);
    }
  }, [open, templateId, dealerId]);

  const handleFetchTemplate = useCallback(async () => {
    if (!dealerId || !templateId) {
      setPreviewError('Dealer ID and Template ID are required');
      return;
    }

    try {
      setPreviewLoading(true);
      setPreviewError(null);
      setPreviewTemplate(null);

      await getEmailTemplateById(dealerId, templateId);
      // The template will be set in selectedTemplate via context
      // We'll update previewTemplate via useEffect when selectedTemplate changes
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch email template';
      setPreviewError(errorMessage);
    } finally {
      setPreviewLoading(false);
    }
  }, [dealerId, templateId, getEmailTemplateById]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          height: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <PreviewDialogTitle>
        <Typography variant="h6">
          {previewTemplate?.name || 'Email Template Preview'}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close preview"
        >
          <Close />
        </IconButton>
      </PreviewDialogTitle>

      <PreviewDialogContent>
        {previewLoading || templateLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <CircularProgress />
          </Box>
        ) : previewError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {previewError}
          </Alert>
        ) : previewTemplate ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
            {previewTemplate.subject && (
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, flexShrink: 0 }}>
                Subject: {previewTemplate.subject}
              </Typography>
            )}
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px',
                padding: 2,
                backgroundColor: 'background.paper',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                minHeight: 0,
              }}
            >
              {previewTemplate.html ? (
                <iframe
                  srcDoc={previewTemplate.html}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    flex: 1,
                  }}
                  title="Email Template Preview"
                  sandbox="allow-same-origin"
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No content available
                </Typography>
              )}
            </Box>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No template selected
          </Typography>
        )}
      </PreviewDialogContent>
    </Dialog>
  );
};

